/**
 * Exportación de la vista de lista a Excel / CSV (pantallas 45 y 46).
 *
 * La regla del diseño es que "el excel sigue lo que hay en la tabla": las
 * mismas columnas visibles, en el mismo orden y con los mismos valores. Por eso
 * las celdas se resuelven con `cellValue`, que es el mismo helper que pinta la
 * tabla.
 */

import { cellValue, columnDefinition } from './listColumns'

export function buildExportRows (tasks, columnKeys, t = null) {
  const header = columnKeys.map(key => {
    const definition = columnDefinition(key)
    if (!definition) return key
    return t ? t(`controlTower.column_${key}`) : definition.label
  })

  const rows = (tasks || []).map(task => columnKeys.map(key => cellValue(task, key, t)))

  return [header, ...rows]
}

/**
 * CSV con comillas y BOM: sin el BOM, Excel en Windows rompe los acentos.
 */
export function toCsv (rows, separator = ',') {
  const escape = value => {
    const text = value === null || value === undefined ? '' : String(value)
    return /["\n\r,;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }

  return '﻿' + rows.map(row => row.map(escape).join(separator)).join('\r\n')
}

export function exportFileName (viewType, extension) {
  const labels = { status: 'por-estatus', user: 'por-usuario', location: 'por-ubicacion' }
  const stamp = new Date().toISOString().slice(0, 10)

  return `torre-de-control-${labels[viewType] || viewType}-${stamp}.${extension}`
}

/**
 * Descarga real. Se importan xlsx-js-style y file-saver de forma perezosa para
 * no meter ~1 MB de librería en el bundle de una pantalla que casi nunca
 * exporta.
 */
export async function downloadTasksFile (tasks, columnKeys, viewType, format = 'xlsx', t = null) {
  const rows = buildExportRows(tasks, columnKeys, t)
  const { saveAs } = await import('file-saver')

  if (format === 'csv') {
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' })
    saveAs(blob, exportFileName(viewType, 'csv'))
    return { rows: rows.length - 1, format: 'csv' }
  }

  const XLSX = await import('xlsx-js-style')
  const sheet = XLSX.utils.aoa_to_sheet(rows)

  sheet['!cols'] = columnKeys.map(() => ({ wch: 22 }))
  rows[0].forEach((_, index) => {
    const address = XLSX.utils.encode_cell({ r: 0, c: index })
    if (sheet[address]) {
      sheet[address].s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '2E3A4B' } },
        alignment: { horizontal: 'center' }
      }
    }
  })

  const book = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(book, sheet, 'Torre de Control')

  const buffer = XLSX.write(book, { bookType: 'xlsx', type: 'array' })
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), exportFileName(viewType, 'xlsx'))

  return { rows: rows.length - 1, format: 'xlsx' }
}
